import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { UserMindX } from '../models/mindx';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private readonly workspaceStorageKeys = ['workspace', 'workpsaceId', 'workspaceId'];
  private workspaceSubject: BehaviorSubject<any | null>;

  constructor(private apollo: Apollo) {
    const savedWorkspace = this.getStoredWorkspace();
    this.workspaceSubject = new BehaviorSubject<any | null>(savedWorkspace);
  }

  getWorkspace(): Observable<any | null> {
    return this.workspaceSubject.asObservable();
  }

  getCurrentWorkspace(): any | null {
    return this.getStoredWorkspace();
  }

  setWorkspace(workspace: any): void {
    const serializedWorkspace = JSON.stringify(workspace);

    for (const key of this.workspaceStorageKeys) {
      sessionStorage.setItem(key, serializedWorkspace);
      localStorage.setItem(key, serializedWorkspace);
    }

    this.workspaceSubject.next(workspace);
  }

  clearWorkspaceId(): void {
    for (const key of this.workspaceStorageKeys) {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    }

    this.workspaceSubject.next(null);
  }

  getOrganizationsByCompanyPlatformId(
    companyPlatformId: string,
    userPlatformId?: string | null
  ): Observable<any[]> {
    const client = this.apollo.use('organizations');
    const trimmedUserPlatformId = userPlatformId != null ? String(userPlatformId).trim() : '';
    const hasUserFilter = trimmedUserPlatformId.length > 0;

    return client.query<{ organizations: { items?: any[] } | any[] }>({
      query: gql`
        query Organizations($companyPlatformId: ID!, $status: Boolean${hasUserFilter ? ', $userPlatformId: ID' : ''}) {
          organizations(
            companyPlatformId: $companyPlatformId,
            status: $status
            ${hasUserFilter ? 'userPlatformId: $userPlatformId' : ''}
          ) {
            items {
              id
              name
              status
            }
          }
        }
      `,
      variables: {
        companyPlatformId,
        status: true,
        ...(hasUserFilter ? { userPlatformId: trimmedUserPlatformId } : {})
      },
      context: {
        headers: this.getAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    }).pipe(
      map(result => this.normalizeCollection(result.data?.organizations))
    );
  }

  getAreasByOrganizationId(
    organizationId: string,
    userPlatformId?: string | null
  ): Observable<any[]> {
    const client = this.apollo.use('areas');
    const trimmedUserPlatformId = userPlatformId != null ? String(userPlatformId).trim() : '';
    const hasUserFilter = trimmedUserPlatformId.length > 0;

    return client.query<{ areas: { items?: any[] } | any[] }>({
      query: gql`
        query Areas($organizationId: ID!, $active: Boolean${hasUserFilter ? ', $userPlatformId: ID' : ''}) {
          areas(
            organizationId: $organizationId,
            active: $active
            ${hasUserFilter ? 'userPlatformId: $userPlatformId' : ''}
          ) {
            items {
              id
              name
              active
            }
          }
        }
      `,
      variables: {
        organizationId,
        active: true,
        ...(hasUserFilter ? { userPlatformId: trimmedUserPlatformId } : {})
      },
      context: {
        headers: this.getAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    }).pipe(
      map(result => this.normalizeCollection(result.data?.areas))
    );
  }

  getWorkspacesByAreaId(
    areaId: string,
    userPlatformId?: string | null
  ): Observable<any[]> {
    const client = this.apollo.use('workspaces');
    const trimmedUserPlatformId = userPlatformId != null ? String(userPlatformId).trim() : '';
    const hasUserFilter = trimmedUserPlatformId.length > 0;

    return client.query<{ workspaces: { items?: any[] } | any[] }>({
      query: gql`
        query Workspaces($areaId: ID!, $active: Boolean${hasUserFilter ? ', $userPlatformId: ID' : ''}) {
          workspaces(
            areaId: $areaId,
            active: $active
            ${hasUserFilter ? 'userPlatformId: $userPlatformId' : ''}
          ) {
            items {
              id
              name
              active
            }
          }
        }
      `,
      variables: {
        areaId,
        active: true,
        ...(hasUserFilter ? { userPlatformId: trimmedUserPlatformId } : {})
      },
      context: {
        headers: this.getAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    }).pipe(
      map(result => this.normalizeCollection(result.data?.workspaces))
    );
  }

  getWorkspaces(userPlatformId: string) {
    return this.apollo.query({
      query: gql`
        query GetUserWorkspaces($userPlatformId: ID!) {
          findUserWorkspacesByUserPlatform(userPlatformId: $userPlatformId) {
            id
            workspace {
              id
              name
              companyPlatformId
            }
          }
        }
      `,
      variables: {
        userPlatformId,
      },
    });
  }

  getUserPlatform(userPlatformId: string) {
    const idStr = String(userPlatformId);
    const client = this.apollo.use('userPlatforms');

    return client.query({
      query: gql`
         query UserPlatform($id: ID!) {
          userPlatform(id: $id) {
            id
            userId
            companyPlatformId
            roleId
            status
            createdAt
          }
        }
      `,
      variables: {
        id: idStr,
      },
      context: {
        headers: this.getAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    });
  }

  getWorkspacesByCompanyPlatformId(companyPlatformId: string) {
    return this.apollo.query({
      query: gql`
        query findWorkspacesByCompanyPlatform($companyPlatformId: ID!) {
          findWorkspacesByCompanyPlatform(id: $companyPlatformId) {
            id
            name
          }
        }
      `,
      variables: {
        companyPlatformId
      }
    });
  }

  createWorkspace(workspace: { name: string; companyPlatformId: string }) {
    return this.apollo.mutate({
      mutation: gql`
        mutation CreateWorkspace($input: [CreateWorkspaceInput!]!) {
          createWorkspace(input: $input) {
            id
            name
            companyPlatformId
          }
        }
      `,
      variables: {
        input: [workspace]
      }
    });
  }

  removeWorkspace(id: string) {
    return this.apollo.mutate<{ removeWorkspace: boolean }>({
      mutation: gql`
        mutation RemoveWorkspace($id: ID!) {
          removeWorkspace(id: $id)
        }
      `,
      variables: { id }
    });
  }

  get_id_mind_x_from_agent(email: string, workspaceId?: string): Observable<string> {
    const normalizedEmail = String(email || '').trim();
    const normalizedWorkspaceId = String(workspaceId || '').trim();

    if (!normalizedEmail || !normalizedWorkspaceId) {
      return new BehaviorSubject<string>('').asObservable();
    }

    const findUsersQuery = gql`
      query FindAllUsers(
        $workspaceId: ID
        $assignmentActive: Boolean
        $search: String
        $skip: Int
        $take: Int
        $orderBy: UserOrderBy
        $orderDirection: OrderDirection
      ) {
        findAllUsers(
          workspaceId: $workspaceId
          assignmentActive: $assignmentActive
          search: $search
          skip: $skip
          take: $take
          orderBy: $orderBy
          orderDirection: $orderDirection
        ) {
          total
          items {
            id
            email
            firstName
            lastName
            document
            documentType
            phone
            status
            enableOTP
          }
        }
      }
    `;

    const authClient = this.apollo.use('users');

    if (!authClient || typeof authClient.query !== 'function') {
      console.error('[WorkspaceService] Apollo named client "users" is not available or invalid.', {
        authClient,
        apolloInstance: this.apollo
      });
      return new BehaviorSubject<string>('').asObservable();
    }

    return authClient.query<{ findAllUsers: { total: number; items: UserMindX[] } }>({
      query: findUsersQuery,
      variables: {
        workspaceId: normalizedWorkspaceId,
        assignmentActive: true,
        search: normalizedEmail,
        skip: 0,
        take: 20,
        orderBy: 'email',
        orderDirection: 'ASC'
      },
      context: {
        headers: this.getAuthorizationHeaders()
      },
      fetchPolicy: 'no-cache'
    }).pipe(
      map(gqlResult => {
        const users = gqlResult.data?.findAllUsers?.items || [];
        const user = users.find(u => String(u.email || '').toLowerCase() === normalizedEmail.toLowerCase());
        return user ? String(user.id) : '';
      }),
    );
  }

  private getStoredWorkspace(): string | null {
    for (const key of this.workspaceStorageKeys) {
      const value = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (value) {
        return value;
      }
    }

    return null;
  }

  private getAuthorizationHeaders(): Record<string, string> {
    const token = localStorage.getItem('token')
      || localStorage.getItem('token-otp')
      || localStorage.getItem('authToken')
      || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private normalizeCollection(collection: { items?: any[] } | any[] | undefined | null): any[] {
    if (!collection) {
      return [];
    }

    if (Array.isArray(collection)) {
      return collection;
    }

    return Array.isArray(collection.items) ? collection.items : [];
  }
}
